import { MMButton, StatusBadge, type StatusBadgeTone } from '@components'
import cn from '@integrations/classnames'
import { formatDisplayDateTime } from '@utils'
import {
  extractCounterpartyName,
  formatTransactionDescription,
  getTransactionStatusName,
  getTransactionTypeName,
  TransactionDirections,
  useDownloadTransactionReceipt
} from 'mmfintech-backend-api'
import type { TransactionViewPortal } from 'mmfintech-backend-api/types'
import { fetchLastChunk, formatMoney, tr } from 'mmfintech-commons'
import { LabelValue } from 'mmfintech-portal-commons'

import { RecurringModalCard, RecurringModalFooter, RecurringModalSurface } from './RecurringModalSurface'
import styles from './RecurringPastTransactionDetailsModal.module.scss'

interface Props {
  transaction: TransactionViewPortal
}

function getTransactionStatusTone(status: string): StatusBadgeTone | undefined {
  const normalizedStatus = status?.toLowerCase()

  if (['pending', 'scheduled', 'initial'].includes(normalizedStatus)) return 'warning'
  if (['failed', 'cancelled'].includes(normalizedStatus)) return 'error'
  if (['processed', 'confirmed'].includes(normalizedStatus)) return 'success'
  return undefined
}

function RecurringPastTransactionDetailsModal({ transaction }: Props) {
  const { downloadReceipt, downloadReceiptFetching } = useDownloadTransactionReceipt()

  const {
    id,
    amount,
    currency,
    direction,
    status,
    reqTime,
    trnTypeLocalizationKey,
    transactionCategory,
    feeDetails,
    accountName,
    paymentMethod,
    reference,
    processingAmount,
    processingCurrency
  } = transaction || {}

  const statusModifier = `status-${status?.toLowerCase()}`

  function handleDownload() {
    downloadReceipt(id)
  }

  return (
    <RecurringModalSurface
      title={tr('FRONTEND.TRANSACTIONS.DETAILS_TITLE', 'Transaction')}
      className={cn('recurring-past-transaction-details-wrapper')}
      data-test='recurring-past-transaction-details-modal'
    >
      <RecurringModalCard
        amount
        className={cn('recurring-past-transaction-details-card recurring-past-transaction-details-amount-card')}
      >
        <div
          className={cn(
            styles['recurring-past-transaction-details-amount-label'],
            'recurring-past-transaction-details-amount-label'
          )}
        >
          {tr('FRONTEND.TRANSACTIONS.COLUMNS.AMOUNT', 'Amount')}
        </div>
        <div className={cn('amount')} data-test='amount'>
          {formatMoney(amount * (direction === TransactionDirections.WITHDRAW ? -1 : 1), currency)}
        </div>
        <StatusBadge
          tone={getTransactionStatusTone(status)}
          className={cn('transaction-status-badge', statusModifier)}
          data-test='status'
        >
          {getTransactionStatusName(status)}
        </StatusBadge>
      </RecurringModalCard>

      <RecurringModalCard className={cn('recurring-past-transaction-details-card')}>
        <LabelValue
          label={tr('FRONTEND.TRANSACTIONS.COLUMNS.TRANSACTION_TYPE', 'Type')}
          value={tr(trnTypeLocalizationKey, fetchLastChunk(trnTypeLocalizationKey))}
          colon
          dataTest='transaction-type'
        />
        <LabelValue
          label={tr('FRONTEND.TRANSACTIONS.COLUMNS.PAYMENT_CATEGORY', 'Payment Category')}
          value={getTransactionTypeName(transactionCategory)}
          colon
          dataTest='payment-category'
        />
        <LabelValue
          label={tr('FRONTEND.TRANSACTIONS.COLUMNS.DATE', 'Date')}
          value={formatDisplayDateTime(reqTime)}
          colon
          dataTest='request-date'
        />
        <LabelValue
          label={tr('FRONTEND.TRANSACTIONS.COLUMNS.FEE', 'Fee')}
          value={feeDetails ? formatMoney(feeDetails.amount, feeDetails.currency) : '0'}
          colon
          dataTest='fee'
        />
        <LabelValue
          label={tr('FRONTEND.TRANSACTIONS.COLUMNS.CUSTOMER', 'Counterparty')}
          value={extractCounterpartyName(transaction)}
          colon
          dataTest='counterparty'
        />
        <LabelValue
          label={tr('FRONTEND.TRANSACTIONS.COLUMNS.ACCOUNT', 'Account')}
          value={accountName}
          colon
          dataTest='account-name'
        />
        <LabelValue label={tr('FRONTEND.TRANSACTIONS.COLUMNS.ID', 'ID')} value={id} colon dataTest='transaction-id' />
        <LabelValue
          label={tr('FRONTEND.TRANSACTIONS.COLUMNS.PAYMENT_METHOD', 'Payment method')}
          value={tr(`METADATA.PAYMENT_OPTIONS.${paymentMethod}`, paymentMethod)}
          colon
          dataTest='payment-method'
        />
        <LabelValue
          label={tr('FRONTEND.TRANSACTIONS.COLUMNS.REFERENCE', 'Reference')}
          value={reference || '—'}
          colon
          dataTest='reference'
        />
        <LabelValue
          label={tr('FRONTEND.TRANSACTIONS.DETAILS.PROCESSING_AMOUNT', 'Processing amount')}
          value={formatMoney(processingAmount, processingCurrency)}
          colon
          dataTest='processing-amount'
        />
        <LabelValue
          label={tr('FRONTEND.TRANSACTIONS.COLUMNS.DESCRIPTION', 'Description')}
          value={formatTransactionDescription(transaction) || '—'}
          colon
          dataTest='description'
        />
      </RecurringModalCard>

      <RecurringModalFooter className={cn('recurring-past-transaction-details-footer')}>
        <MMButton
          type='button'
          variant='dashboard'
          caption={tr('FRONTEND.TRANSACTIONS.DOWNLOAD_RECEIPT', 'Download as PDF')}
          onClick={handleDownload}
          loading={downloadReceiptFetching}
          data-test='button-download-receipt'
        />
      </RecurringModalFooter>
    </RecurringModalSurface>
  )
}

export default RecurringPastTransactionDetailsModal
